import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2, FileText, TrendingUp, DollarSign, ArrowLeft, Mail, RefreshCw, Trash2 } from "lucide-react";
import { BlogPostCard } from "@/components/BlogPostCard";
import { AutomationStatus } from "@/components/AutomationStatus";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";

export default function BlogGenerator() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedPost, setGeneratedPost] = useState<any>(null);
  const [existingArticles, setExistingArticles] = useState<any[]>([]);
  const [loadingArticles, setLoadingArticles] = useState(true);
  const [currentSelections, setCurrentSelections] = useState<any>(null);
  const [loadingSelections, setLoadingSelections] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadExistingArticles();
  }, []);

  const loadExistingArticles = async () => {
    try {
      const { data, error } = await supabase
        .from('articles')
        .select('id, headline, category, publish_date, status, content, word_count, opportunity_score, primary_token')
        .order('publish_date', { ascending: false })
        .limit(10);

      if (error) throw error;
      setExistingArticles(data || []);
    } catch (error) {
      console.error('Failed to load articles:', error);
    } finally {
      setLoadingArticles(false);
    }
  };

  const loadCurrentSelections = async () => {
    setLoadingSelections(true);
    try {
      const { data, error } = await supabase.functions.invoke('select-opportunities');

      if (error) {
        console.error('Failed to load selections:', error);
        // Don't show error toast, just log it - data collection hasn't run yet
        setLoadingSelections(false);
        return;
      }

      setCurrentSelections(data);
      toast({
        title: "Selections Loaded",
        description: `Found ${data.metadata.total_viable} viable opportunities`,
      });
    } catch (error: any) {
      console.error('Failed to load selections:', error);
      toast({
        title: "Error",
        description: "Failed to preview selections",
        variant: "destructive",
      });
    } finally {
      setLoadingSelections(false);
    }
  };

  const handleGenerate = async (postType: 'best_coin' | 'best_coin_today' | 'daily_market' | 'deep_dive') => {
    setIsGenerating(true);
    setGeneratedPost(null);

    try {
      console.log(`🚀 Generating ${postType} blog post...`);
      
      // First, select opportunities
      console.log('📊 Selecting opportunities...');
      const { data: selections, error: selectError } = await supabase.functions.invoke('select-opportunities');
      
      if (selectError) {
        console.error('Selection error:', selectError);
        throw new Error('No market data available yet. Please run "Data Collection" from the homepage first to populate the database with crypto market data.');
      }
      
      console.log('✅ Opportunities selected:', selections);
      
      // Then generate blog post with selected opportunities
      const { data, error } = await supabase.functions.invoke('generate-blog-post', {
        body: { 
          postType,
          selectedOpportunities: selections
        }
      });

      if (error) {
        console.error('Generation error:', error);
        throw error;
      }

      console.log('✅ Blog post generated:', data);
      
      const postTypeLabel = postType === 'best_coin' ? 'Best Coin' : 
                           postType === 'best_coin_today' ? 'Best Coin Today' :
                           postType === 'deep_dive' ? 'Deep Dive Analysis' : 
                           'Daily Market Overview';
      
      toast({
        title: "Blog Post Generated!",
        description: `Successfully created ${postTypeLabel} article (Quality: ${data.quality_score}/100)`,
      });

      setGeneratedPost(data.article);
      await loadExistingArticles(); // Refresh the list
    } catch (error: any) {
      console.error('Failed to generate blog post:', error);
      toast({
        title: "Generation Failed",
        description: error.message || "Failed to generate blog post. Check the console for details.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerateArticle = async (articleId: string, postType: string) => {
    if (!confirm('This will delete the existing article and generate a new one. Continue?')) {
      return;
    }

    setIsGenerating(true);

    try {
      // Delete the old article
      const { error: deleteError } = await supabase
        .from('articles')
        .delete()
        .eq('id', articleId);

      if (deleteError) throw deleteError;

      // Generate new article
      const { data, error } = await supabase.functions.invoke('generate-blog-post', {
        body: { postType }
      });

      if (error) throw error;

      toast({
        title: "Article Regenerated!",
        description: `New version created (Quality: ${data.quality_score}/100)`,
      });

      await loadExistingArticles();
      setGeneratedPost(data.article);
    } catch (error: any) {
      console.error('Failed to regenerate article:', error);
      toast({
        title: "Regeneration Failed",
        description: error.message || "Failed to regenerate article",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDeleteArticle = async (articleId: string) => {
    if (!confirm('Are you sure you want to delete this article?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('articles')
        .delete()
        .eq('id', articleId);

      if (error) throw error;

      toast({
        title: "Article Deleted",
        description: "Article removed successfully",
      });

      await loadExistingArticles();
    } catch (error: any) {
      console.error('Failed to delete article:', error);
      toast({
        title: "Delete Failed",
        description: error.message || "Failed to delete article",
        variant: "destructive",
      });
    }
  };

  const handleSendNewsletter = async () => {
    const testEmail = prompt("Enter test email address:");
    if (!testEmail) return;

    setIsGenerating(true);

    try {
      console.log(`📧 Sending newsletter to ${testEmail}...`);
      
      const { data, error } = await supabase.functions.invoke('send-newsletter', {
        body: { test_email: testEmail }
      });

      if (error) {
        console.error('Newsletter error:', error);
        throw error;
      }

      console.log('✅ Newsletter sent:', data);
      
      toast({
        title: "Newsletter Sent!",
        description: `Successfully sent to ${testEmail}`,
      });
    } catch (error: any) {
      console.error('Failed to send newsletter:', error);
      toast({
        title: "Send Failed",
        description: error.message || "Failed to send newsletter. Check the console for details.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <div className="container mx-auto px-4 py-12 max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="ghost" size="sm" className="mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
          <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            🧪 Blog Post Generator
          </h1>
          <p className="text-muted-foreground">
            Test the Sydneygotoman article format by generating blog posts manually
          </p>
        </div>

        {/* Full Automation Pipeline */}
        <div className="mb-8">
          <AutomationStatus />
        </div>

        {/* Existing Articles */}
        <Card className="p-6 mb-8 bg-card/50 backdrop-blur-sm border-primary/20">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Existing Articles
          </h2>

          {loadingArticles ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : existingArticles.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No articles found</p>
          ) : (
            <div className="space-y-4">
              {existingArticles.map((article) => (
                <Card key={article.id} className="p-4 border border-border/50 hover:border-primary/50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <Link to={`/article/${article.id}`}>
                        <h3 className="font-semibold mb-1 hover:text-primary transition-colors cursor-pointer">
                          {article.headline}
                        </h3>
                      </Link>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span>{article.category}</span>
                        <span>•</span>
                        <span>{format(new Date(article.publish_date), 'MMM dd, yyyy')}</span>
                        <span>•</span>
                        <span className="capitalize">{article.status}</span>
                        <span>•</span>
                        <span>{article.word_count || 0} words</span>
                        {article.opportunity_score && (
                          <>
                            <span>•</span>
                            <span>Score: {article.opportunity_score}</span>
                          </>
                        )}
                        {article.primary_token && (
                          <>
                            <span>•</span>
                            <span className="font-semibold text-primary">{article.primary_token}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRegenerateArticle(article.id, 'market_analysis')}
                        disabled={isGenerating}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Regenerate
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteArticle(article.id)}
                        disabled={isGenerating}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Card>

        {/* Generation Controls */}
        <Card className="p-6 mb-8 bg-card/50 backdrop-blur-sm border-primary/20">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Generate New Article
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              size="lg"
              onClick={() => handleGenerate('best_coin_today')}
              disabled={isGenerating}
              className="h-auto py-6 flex flex-col items-center gap-2"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <TrendingUp className="w-6 h-6" />
                  <div className="text-center">
                    <div className="font-bold">Best Coin to Buy Today</div>
                    <div className="text-xs opacity-80">Daily top pick analysis</div>
                  </div>
                </>
              )}
            </Button>

            <Button
              size="lg"
              onClick={() => handleGenerate('deep_dive')}
              disabled={isGenerating}
              className="h-auto py-6 flex flex-col items-center gap-2"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <DollarSign className="w-6 h-6" />
                  <div className="text-center">
                    <div className="font-bold">Deep Dive Analysis</div>
                    <div className="text-xs opacity-80">Comprehensive token research</div>
                  </div>
                </>
              )}
            </Button>

            <Button
              size="lg"
              onClick={() => handleGenerate('daily_market')}
              disabled={isGenerating}
              className="h-auto py-6 flex flex-col items-center gap-2 bg-gradient-to-br from-accent to-accent/80"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <FileText className="w-6 h-6" />
                  <div className="text-center">
                    <div className="font-bold">Daily Market Overview</div>
                    <div className="text-xs opacity-80">Market summary & insights</div>
                  </div>
                </>
              )}
            </Button>
          </div>

          <Button
            size="lg"
            onClick={handleSendNewsletter}
            disabled={isGenerating}
            variant="outline"
            className="h-auto py-6 flex flex-col items-center gap-2 border-accent/50 mt-4"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin" />
                <span>Sending...</span>
              </>
            ) : (
              <>
                <Mail className="w-6 h-6 text-accent" />
                <div className="text-center">
                  <div className="font-bold">Send Test Newsletter</div>
                  <div className="text-xs opacity-80">Email digest</div>
                </div>
              </>
            )}
          </Button>

          <div className="mt-4 p-4 bg-muted/50 rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> Generation uses the latest AI analysis data from the database. 
              Make sure the daily automation has run to get fresh market data.
            </p>
          </div>
        </Card>

        {/* Generated Post Preview */}
        {generatedPost && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold">📝 Generated Article</h2>
            
            <Card className="p-6 bg-card/50 backdrop-blur-sm border-border/50">
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="default" className="bg-gradient-primary">
                        {generatedPost.category}
                      </Badge>
                      {generatedPost.primary_token && (
                        <Badge variant="outline" className="border-primary/30">
                          <TrendingUp className="w-3 h-3 mr-1" />
                          {generatedPost.primary_token}
                        </Badge>
                      )}
                    </div>
                    <h3 className="text-2xl font-bold text-foreground mb-2">
                      {generatedPost.headline}
                    </h3>
                    <p className="text-muted-foreground">
                      {generatedPost.deck || generatedPost.summary}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-sm text-muted-foreground pt-4 border-t border-border/30">
                  <span>{generatedPost.word_count || 0} words</span>
                  <span>•</span>
                  <span>{generatedPost.reading_time || 0} min read</span>
                  {generatedPost.opportunity_score && (
                    <>
                      <span>•</span>
                      <span>Score: {generatedPost.opportunity_score}</span>
                    </>
                  )}
                </div>
              </div>
            </Card>
            
            <Card className="p-6 bg-card/50 backdrop-blur-sm">
              <h3 className="text-lg font-semibold mb-4">Article Content</h3>
              <div 
                className="prose prose-sm max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ 
                  __html: DOMPurify.sanitize(generatedPost.content, {
                    ALLOWED_TAGS: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'strong', 'em', 'u', 'ul', 'ol', 'li', 'a', 'blockquote', 'br', 'span'],
                    ALLOWED_ATTR: ['href', 'target', 'rel', 'class']
                  })
                }}
              />
            </Card>
          </div>
        )}

        {/* Info Card */}
        {!generatedPost && !isGenerating && (
          <Card className="p-6 bg-card/50 backdrop-blur-sm border-accent/20">
            <h3 className="text-lg font-semibold mb-2 text-accent">
              ℹ️ How It Works
            </h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• Fetches latest AI analysis from the database</li>
              <li>• Generates Sydneygotoman-style article with emojis and Australian focus</li>
              <li>• Includes affiliate link placeholders for CoinSpot, Swyftx, Independent Reserve</li>
              <li>• Stores blog post in database with published status</li>
              <li>• SEO-optimized with proper meta tags and keywords</li>
            </ul>
          </Card>
        )}
      </div>
    </div>
  );
}
